import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import { SENSITIVE_DATA_API_SERVICE_TOKEN, WATCHLIST_API_SERVICE_TOKEN } from '@nk/api-lib';
import { AppFeatureKey } from '@nk/data-entities-lib';
import { CardComponent, CardContentComponent, CardHeaderComponent } from '@nk/ui-lib';
import { LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { AccountsNumberPipe } from '../../pipes/accounts-number.pipe';
import { CollectionCaseService } from '../../services/collection-case.service';
import { CollectionCaseCompositeData, getCollectionCaseCompositeData } from '../../utils/company-payment-remarks.utils';
import { getCompanySensitiveDataAccess } from '../../utils/company-sensitive-data-access.utils';
import { PaymentRemarksSectionComponent } from '../payment-remarks-section/payment-remarks-section.component';
import { CompanyPaymentRemarksChartComponent } from './components/company-payment-remarks-chart/company-payment-remarks-chart.component';
import { CompanyPaymentRemarksTableComponent } from './components/company-payment-remarks-table/company-payment-remarks-table.component';

@Component({
  selector: 'nk-company-payment-remarks-card',
  templateUrl: './company-payment-remarks-card.component.html',
  imports: [
    TranslateModule,
    AccountsNumberPipe,
    CardComponent,
    CardHeaderComponent,
    CardContentComponent,
    CompanyPaymentRemarksChartComponent,
    CompanyPaymentRemarksTableComponent,
    PaymentRemarksSectionComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyPaymentRemarksCardComponent implements OnInit, OnDestroy {
  public companyUuid = input.required<string>();
  public printVersion = input(false);

  public hasFeatureAndRoleAccess = signal<boolean>(false);
  public sensitiveDataAccess = signal<boolean | null | undefined>(undefined);
  public collectionCaseCompositeData = signal<CollectionCaseCompositeData | null>(null);

  private _sensitiveDataService = inject(SENSITIVE_DATA_API_SERVICE_TOKEN);
  private _collectionCaseService = inject(CollectionCaseService);
  private _appService = inject(AppService);
  private _watchlistService = inject(WATCHLIST_API_SERVICE_TOKEN);
  private _destroyRef = inject(DestroyRef);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });

  public ngOnInit(): void {
    this._sensitiveDataService
      .hasFeatureAndRoleAccess(AppFeatureKey.payment_remark_company_overview)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => this.hasFeatureAndRoleAccess.set(value));

    const companyUuid = this.companyUuid();
    if (companyUuid) {
      this._reportLoadingStateService?.setLoadingState(this.constructor.name, true);

      const sensitiveDataAccess$ = this._sensitiveDataService.loadTriggered$.pipe(
        switchMap(() => this._appService.identityDataCache.get()),
        switchMap((identity) =>
          getCompanySensitiveDataAccess(
            companyUuid,
            identity.app_customer.uuid,
            this._sensitiveDataService,
            this._watchlistService,
          ),
        ),
        map((hasAccess) => (hasAccess ? true : null)),
      );

      sensitiveDataAccess$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
        this.sensitiveDataAccess.set(value);
      });

      sensitiveDataAccess$
        .pipe(
          filter((access) => access === true),
          switchMap(() =>
            this._collectionCaseService.collectionCaseCompositeEntityListCache
              .get({
                debtorCompanyIdentifier: companyUuid,
              })
              .pipe(
                catchError((error: unknown) => {
                  console.error('Failed to load collection cases, hiding payment remarks section', error);
                  return of(null);
                }),
              ),
          ),
          map((response) => (response ? getCollectionCaseCompositeData(response) : null)),
          takeUntilDestroyed(this._destroyRef),
        )
        .subscribe((data) => {
          this.collectionCaseCompositeData.set(data);
          this._setLoadingDone();
        });

      sensitiveDataAccess$
        .pipe(
          filter((access) => access === null),
          takeUntilDestroyed(this._destroyRef),
        )
        .subscribe(() => this._setLoadingDone());
    }
  }

  private _setLoadingDone(): void {
    this._reportLoadingStateService?.setLoadingState(this.constructor.name, false);
  }

  public ngOnDestroy(): void {
    this._collectionCaseService.collectionCaseCompositeEntityListCache.invalidateAllCache();
  }
}
